import { useState } from 'react'
import { Pencil, TriangleAlert, FileWarning } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { IncidentFormDialog } from './IncidentFormDialog'
import { useUpdateVehicle } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import {
  type FleetRow,
  LIFECYCLE_STATUS_OPTIONS,
  HOLD_REASON_OPTIONS,
  LOCATION_OPTIONS,
  isActive,
  inactiveReasons,
  rowActiveInput,
} from '../../utils/fleet'

/** The correction route needs a sentence. The database enforces the same floor. */
const CORRECTION_MIN = 10

function toDateInput(value: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

function nullIfEmpty(value: string): string | null {
  const t = value.trim()
  return t === '' ? null : t
}

function numOrNull(value: string): number | null {
  const t = value.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/**
 * Edit one vehicle, and refuse to take it off the road quietly.
 *
 * Luyanda, 1 September 2026: "If a car needs to go inactive, before allowing it,
 * make a big warning that an incident report is required for that and then
 * surface the incident report form and require an incident report."
 *
 * Active is not a field on this form. It is what the four conditions add up to,
 * recomputed live as the operator types, and shown at the top so the consequence
 * of a change is visible before it is saved rather than discovered on the list
 * afterwards. When a save would cross a car from active to inactive the dialog
 * stops and asks for one of two things:
 *
 *   the incident that took the car off the road, filed in full, or
 *   a sentence saying which of these values had been captured wrongly.
 *
 * The second route exists on purpose. Without it, fixing a location somebody
 * mistyped would require filing an accident report, and within a week the
 * incident log would be full of empty reports written to get past a dialog. It
 * is an audited exception, not a bypass: it lands in vehicle_active_change with
 * the actor against it. update_vehicle_v1 enforces both routes, so this screen
 * is the courtesy and the database is the rule.
 */
export function EditVehicleDialog({
  vehicle,
  onSaved,
}: {
  vehicle: FleetRow
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const update = useUpdateVehicle()

  // Form state, seeded from the current row each time the dialog opens.
  const [status, setStatus] = useState(vehicle.lifecycle_status)
  const [operational, setOperational] = useState(vehicle.operational === true)
  const [inGeneralFleet, setInGeneralFleet] = useState(vehicle.in_general_fleet === true)
  const [holdReasons, setHoldReasons] = useState<string[]>(vehicle.hold_reasons ?? [])
  const [location, setLocation] = useState(vehicle.location ?? '')
  const [telematics, setTelematics] = useState(vehicle.telematics_status ?? '')
  const [licensing, setLicensing] = useState(vehicle.licensing_status ?? '')
  const [compliance, setCompliance] = useState(vehicle.compliance_status ?? '')
  const [nextService, setNextService] = useState(toDateInput(vehicle.next_service_date))
  const [licenseExpiry, setLicenseExpiry] = useState(toDateInput(vehicle.license_expiry))
  const [repairsDue, setRepairsDue] = useState(vehicle.repairs_due ?? '')
  const [financed, setFinanced] = useState(vehicle.financed === true)
  const [financier, setFinancier] = useState(vehicle.financier ?? '')
  const [financeMonths, setFinanceMonths] = useState(
    vehicle.finance_months_left == null ? '' : String(vehicle.finance_months_left),
  )
  const [instalment, setInstalment] = useState(
    vehicle.instalment_zar == null ? '' : String(vehicle.instalment_zar),
  )

  // The gate. `warning` shows the stop screen; `correction` is the typed sentence
  // for the correction route; `incidentOpen` hands over to the incident form.
  const [warning, setWarning] = useState(false)
  const [correction, setCorrection] = useState('')
  const [incidentOpen, setIncidentOpen] = useState(false)

  function reseed() {
    setStatus(vehicle.lifecycle_status)
    setOperational(vehicle.operational === true)
    setInGeneralFleet(vehicle.in_general_fleet === true)
    setHoldReasons(vehicle.hold_reasons ?? [])
    setLocation(vehicle.location ?? '')
    setTelematics(vehicle.telematics_status ?? '')
    setLicensing(vehicle.licensing_status ?? '')
    setCompliance(vehicle.compliance_status ?? '')
    setNextService(toDateInput(vehicle.next_service_date))
    setLicenseExpiry(toDateInput(vehicle.license_expiry))
    setRepairsDue(vehicle.repairs_due ?? '')
    setFinanced(vehicle.financed === true)
    setFinancier(vehicle.financier ?? '')
    setFinanceMonths(vehicle.finance_months_left == null ? '' : String(vehicle.finance_months_left))
    setInstalment(vehicle.instalment_zar == null ? '' : String(vehicle.instalment_zar))
    setWarning(false)
    setCorrection('')
  }

  function toggleHold(code: string) {
    setHoldReasons((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    )
  }

  const pending = {
    lifecycle_status: status,
    in_general_fleet: inGeneralFleet,
    hold_reasons: holdReasons,
    operational,
    location: nullIfEmpty(location),
  }
  const wasActive = isActive(rowActiveInput(vehicle))
  const willBeActive = isActive(pending)
  const goingInactive = wasActive && !willBeActive
  const reasons = inactiveReasons(pending)

  const plate = vehicle.registration_number || 'this vehicle'

  function buildPatch(justification: Record<string, unknown>) {
    return {
      status,
      operational,
      in_general_fleet: inGeneralFleet,
      hold_reasons: holdReasons,
      current_location: nullIfEmpty(location),
      telematics_status: nullIfEmpty(telematics),
      licensing_status: nullIfEmpty(licensing),
      compliance_status: nullIfEmpty(compliance),
      next_service_date: nullIfEmpty(nextService),
      license_expiry: nullIfEmpty(licenseExpiry),
      repairs_due: nullIfEmpty(repairsDue),
      financed,
      financier: nullIfEmpty(financier),
      finance_months_left: numOrNull(financeMonths),
      instalment_zar: numOrNull(instalment),
      ...justification,
    }
  }

  async function save(justification: Record<string, unknown>) {
    try {
      await update.trigger({ vehicle_id: vehicle.vehicle_id, patch: buildPatch(justification) })
        .result
      toast.success('Vehicle updated')
      setOpen(false)
      setWarning(false)
      setCorrection('')
      onSaved()
    } catch (err) {
      toast.error('Could not update vehicle', { description: writeErrorMessage(err) })
    }
  }

  function handleSave() {
    if (goingInactive) {
      setWarning(true)
      return
    }
    void save({})
  }

  // Hand over to the incident form. The edit dialog closes first: two stacked
  // modals fight over the focus trap, and the operator has one job at a time.
  function openIncident() {
    setOpen(false)
    setIncidentOpen(true)
  }

  // The incident has already written operational, the hold reason and the
  // location onto the vehicle. Applying the rest of the edit now with the
  // incident attached is what puts the operator's other changes through and what
  // links this crossing to the report in vehicle_active_change.
  function onIncidentRecorded(incidentId: string | null) {
    setIncidentOpen(false)
    if (!incidentId) {
      onSaved()
      return
    }
    void save({ incident_id: incidentId })
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) reseed()
          setOpen(next)
        }}
      >
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
            <Pencil className="h-3.5 w-3.5" />
            Edit vehicle
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit vehicle</DialogTitle>
            <DialogDescription>
              {vehicle.registration_number || 'Vehicle'} ·{' '}
              {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Unknown vehicle'}
            </DialogDescription>
          </DialogHeader>

          {warning ? (
            <InactivationWarning
              plate={plate}
              reasons={reasons}
              correction={correction}
              onCorrectionChange={setCorrection}
              onBack={() => setWarning(false)}
              onIncident={openIncident}
              onCorrect={() => void save({ correction_reason: correction.trim() })}
              saving={update.loading}
            />
          ) : (
            <>
              <ActiveBanner
                wasActive={wasActive}
                willBeActive={willBeActive}
                reasons={reasons}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="veh-status">Status</Label>
                  <NativeSelect
                    id="veh-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {LIFECYCLE_STATUS_OPTIONS.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.label}
                      </option>
                    ))}
                  </NativeSelect>
                  <p className="text-[11px] text-muted-foreground">
                    The stage this vehicle has reached. Whether it is active today is worked out
                    from the four fields below.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label>Hold reasons</Label>
                  <div className="space-y-1.5 rounded-md border border-border px-3 py-2">
                    {HOLD_REASON_OPTIONS.map((o) => (
                      <div key={o.code} className="flex items-center gap-2">
                        <Checkbox
                          id={`veh-hold-${o.code}`}
                          checked={holdReasons.includes(o.code)}
                          onCheckedChange={() => toggleHold(o.code)}
                        />
                        <Label
                          htmlFor={`veh-hold-${o.code}`}
                          className="cursor-pointer font-normal"
                        >
                          {o.label}
                        </Label>
                      </div>
                    ))}
                    {holdReasons.length === 0 && (
                      <p className="text-[11px] text-muted-foreground">
                        No hold. Tick both if both apply.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <Label htmlFor="veh-operational" className="cursor-pointer font-normal">
                    Operational
                  </Label>
                  <Switch
                    id="veh-operational"
                    checked={operational}
                    onCheckedChange={setOperational}
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <Label htmlFor="veh-general" className="cursor-pointer font-normal">
                    In general fleet
                  </Label>
                  <Switch
                    id="veh-general"
                    checked={inGeneralFleet}
                    onCheckedChange={setInGeneralFleet}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-location">Current location</Label>
                  <NativeSelect
                    id="veh-location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  >
                    <option value="">Not recorded</option>
                    {LOCATION_OPTIONS.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                    {/* A value left over from before the vocabulary changed stays
                        selectable so saving an unrelated field does not silently
                        move the car. Choosing anything else retires it. */}
                    {location !== '' && !LOCATION_OPTIONS.includes(location) && (
                      <option value={location}>{location} (no longer offered)</option>
                    )}
                  </NativeSelect>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-telematics">Telematics status</Label>
                  <Input
                    id="veh-telematics"
                    value={telematics}
                    onChange={(e) => setTelematics(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-licensing">Licensing status</Label>
                  <Input
                    id="veh-licensing"
                    value={licensing}
                    onChange={(e) => setLicensing(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-compliance">Compliance status</Label>
                  <Input
                    id="veh-compliance"
                    value={compliance}
                    onChange={(e) => setCompliance(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-service">Next service date</Label>
                  <Input
                    id="veh-service"
                    type="date"
                    value={nextService}
                    onChange={(e) => setNextService(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-license-exp">License expiry</Label>
                  <Input
                    id="veh-license-exp"
                    type="date"
                    value={licenseExpiry}
                    onChange={(e) => setLicenseExpiry(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="veh-repairs">Repairs due</Label>
                  <Input
                    id="veh-repairs"
                    value={repairsDue}
                    onChange={(e) => setRepairsDue(e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 sm:col-span-2">
                  <Label htmlFor="veh-financed" className="cursor-pointer font-normal">
                    Financed
                  </Label>
                  <Switch id="veh-financed" checked={financed} onCheckedChange={setFinanced} />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-financier">Financier</Label>
                  <Input
                    id="veh-financier"
                    value={financier}
                    onChange={(e) => setFinancier(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-months">Finance months left</Label>
                  <Input
                    id="veh-months"
                    type="number"
                    value={financeMonths}
                    onChange={(e) => setFinanceMonths(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="veh-instalment">Instalment (ZAR)</Label>
                  <Input
                    id="veh-instalment"
                    type="number"
                    value={instalment}
                    onChange={(e) => setInstalment(e.target.value)}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={update.loading}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={update.loading}>
                  {update.loading && <Spinner className="h-4 w-4" />}
                  {goingInactive ? 'Continue' : 'Save changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <IncidentFormDialog
        vehicle={vehicle}
        hideTrigger
        controlledOpen={incidentOpen}
        onControlledOpenChange={setIncidentOpen}
        seedOperational={operational}
        seedLocation={nullIfEmpty(location) ?? undefined}
        onRecorded={onIncidentRecorded}
        onSaved={onSaved}
      />
    </>
  )
}

function ActiveBanner({
  wasActive,
  willBeActive,
  reasons,
}: {
  wasActive: boolean
  willBeActive: boolean
  reasons: string[]
}) {
  const changed = wasActive !== willBeActive
  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          After this save
        </span>
        <Chip tone={willBeActive ? 'green' : 'red'}>{willBeActive ? 'Active' : 'Inactive'}</Chip>
        {changed && (
          <span className="text-[11px] text-muted-foreground">
            was {wasActive ? 'Active' : 'Inactive'}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {willBeActive
          ? 'In the general fleet, no hold, operational, standing at Operating.'
          : reasons.join('; ')}
      </p>
    </div>
  )
}

function InactivationWarning({
  plate,
  reasons,
  correction,
  onCorrectionChange,
  onBack,
  onIncident,
  onCorrect,
  saving,
}: {
  plate: string
  reasons: string[]
  correction: string
  onCorrectionChange: (v: string) => void
  onBack: () => void
  onIncident: () => void
  onCorrect: () => void
  saving: boolean
}) {
  const canCorrect = correction.trim().length >= CORRECTION_MIN && !saving
  return (
    <div className="space-y-4">
      <div className="rounded-md border-2 border-destructive/40 bg-destructive/10 p-4">
        <div className="flex items-start gap-2">
          <TriangleAlert className="mt-0.5 h-6 w-6 shrink-0 text-destructive" />
          <div className="space-y-1">
            <p className="text-base font-semibold text-destructive">
              This takes {plate} off the road
            </p>
            <p className="text-sm text-destructive/90">
              The car stops earning from the moment you save this. Its drivers stay on a guaranteed
              salary either way, so a car that goes inactive without a reason on the record is a
              cost nobody can explain later. An incident report is required.
            </p>
          </div>
        </div>
        <ul className="mt-3 space-y-0.5 pl-8 text-sm text-destructive/90">
          {reasons.map((r) => (
            <li key={r}>· {r}</li>
          ))}
        </ul>
      </div>

      <div className="rounded-md border border-border p-3">
        <p className="text-sm font-medium">File the incident report</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          The full report, the same questions the incident form asks. It records what happened, it
          reaches the owner, and it is what links this car coming off the road to a cause.
        </p>
        <Button className="mt-2 gap-1.5" onClick={onIncident} disabled={saving}>
          <FileWarning className="h-4 w-4" />
          Open the incident report
        </Button>
      </div>

      <div className="rounded-md border border-dashed border-border p-3">
        <p className="text-sm font-medium">Or: one of these values was captured wrongly</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Only for a correction. Nothing happened to the car, the register was simply wrong. Say
          what was recorded and what it should have been, because this is what somebody reads in six
          months when they ask why the car came off the road. It is saved against your name.
        </p>
        <Textarea
          className="mt-2"
          rows={2}
          value={correction}
          onChange={(e) => onCorrectionChange(e.target.value)}
          placeholder="e.g. Location was captured as Operating in error, the car has been at the office since Thursday"
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            {correction.trim().length < CORRECTION_MIN
              ? 'A sentence, not a word.'
              : 'Saved to the vehicle activity register.'}
          </span>
          <Button variant="outline" onClick={onCorrect} disabled={!canCorrect}>
            {saving && <Spinner className="h-4 w-4" />}
            Save as a correction
          </Button>
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onBack} disabled={saving}>
          Back to the form
        </Button>
      </DialogFooter>
    </div>
  )
}
